import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
  getProvidersEmailRecipients,
  performProviderMassAction,
} from '../../../../api'
import { EMPTY_OBJECT } from '../../../../constants/common'
import { useMassActions, useNotification } from '../../../../hooks'
import {
  getMassActionExcludeIdParam,
  getMassActionIdParam,
} from '../../../../utils/common'
import { stopPropagation } from '../../../../utils/events'
import { getIdFilterParam } from '../../../../utils/filter'
import { ConfirmationModal } from '../../../Confirmation'
import { createEmailThread, useSendEmail } from '../../../Email'
import { EditBuildingAndCategoryModal } from '../../EditBuildingAndCategoryModal'
import {
  PROVIDER_ACTION_ARCHIVE,
  PROVIDER_ACTION_ARCHIVE_MASS,
  PROVIDER_ACTION_EDIT_BUILDINGS_AND_CATEGORIES,
  PROVIDER_ACTION_EDIT_BUILDINGS_AND_CATEGORIES_MASS,
  PROVIDER_ACTION_MASS_EMAIL,
  PROVIDER_ACTION_RESTORE,
  PROVIDER_ACTION_RESTORE_MASS,
  PROVIDER_MASS_ACTION_NAMES,
} from '../../Provider.constants'
import {
  getProviderTableAsyncNotificationText,
  getProviderTableItemActions,
  getProviderTableNotificationTitle,
} from '../../Provider.utils'

import {
  createProviderMappingFromFormValues,
  getProviderTableEmailPayload,
} from './useProviderTableActions.utils'

export function useProviderTableActions({
  excludeIds,
  filterValues,
  pagination,
  selectAllPages,
  selectedIds,
  sub,
  onCleanSelection,
  onCloseEmailFormModal,
  onCloseModal,
  onReload,
  onSetEmailFormModal,
  onSetModal,
}) {
  const { t } = useTranslation()

  const [isPerformingMassAction, setPerformingMassAction] = useState(false)

  const { handleAddErrorNotification } = useNotification()

  const {
    getAsyncTaskId,
    handleAddAsyncMassActionNotification,
    handleAddFinishMassActionNotification,
  } = useMassActions({
    getAsyncNotificationText: getProviderTableAsyncNotificationText,
    getNotificationTitle: getProviderTableNotificationTitle,
  })

  function handleErrorAction() {
    onCloseModal()

    handleAddErrorNotification('Notification.ProvidersMassActionErrorKey')
  }

  async function handleSubmitEmail(payload, values) {
    return createEmailThread(getProviderTableEmailPayload({ payload, values }))
  }

  const { handleOpenCreationEmail } = useSendEmail({
    excludeIds,
    filterValues,
    getRecipients: getProvidersEmailRecipients,
    selectAllPages,
    selectedIds,
    onCloseEmailFormModal,
    onCloseModal,
    onSetEmailFormModal,
    onSetModal,
    onSubmit: handleSubmitEmail,
  })

  function massAction({ action, ids, value = EMPTY_OBJECT }) {
    const id = getMassActionIdParam({ ids, selectAllPages, selectedIds })

    const exclude_id = getMassActionExcludeIdParam({
      excludeIds,
      selectAllPages,
    })

    const params = {
      action: PROVIDER_MASS_ACTION_NAMES[action],
      all: selectAllPages,
      filters: {
        ...filterValues,
        exclude_id,
        id: getIdFilterParam({
          filterValues,
          ids: id,
          selectAllPages,
        }),
      },
      payload: value,
    }

    const countOfItems = selectAllPages ? pagination.count : id.length

    onCloseModal()

    setPerformingMassAction(true)

    performProviderMassAction(params)
      .then(({ data }) => {
        onCleanSelection()

        const asyncTask = getAsyncTaskId(data)

        if (asyncTask) {
          handleAddAsyncMassActionNotification({
            action,
            pathname: location.pathname,
            sub,
            task: asyncTask,
            totalCount: countOfItems,
          })

          return
        }

        onReload()

        handleAddFinishMassActionNotification(data, {
          action,
          totalCount: countOfItems,
        })
      })
      .catch(() => {
        handleErrorAction()
      })
      .finally(() => {
        setPerformingMassAction(false)
      })
  }

  function getHandleSubmitEditMapping({ action, id }) {
    return ({ values }) => {
      const mapping = createProviderMappingFromFormValues(values)

      massAction({
        ...(id ? { ids: [id] } : null),
        action,
        value: { mapping },
      })
    }
  }

  function showConfirmModal(title, onOk, btnText) {
    onSetModal(
      <ConfirmationModal
        actionButtonsProps={{
          submitText: btnText,
        }}
        description={title}
        onCancel={onCloseModal}
        onSubmit={onOk}
      />
    )
  }

  function showEditBuildingAndCategoryModal({ action, id, name }) {
    onSetModal(
      <EditBuildingAndCategoryModal
        id={id}
        name={name}
        onCloseModal={onCloseModal}
        onSubmit={getHandleSubmitEditMapping({
          action,
          id,
        })}
      />
    )
  }

  function setItemAction(action, item) {
    switch (action) {
      case PROVIDER_ACTION_RESTORE:
        showConfirmModal(
          t('Modal.ProviderRestoreConfirmationTextKey'),
          e => {
            stopPropagation(e)
            massAction({ action, ids: item.id })
          },
          t('Modal.ProviderRestoreBtnKey')
        )
        break
      case PROVIDER_ACTION_ARCHIVE:
        showConfirmModal(
          t('Modal.ProviderArchiveConfirmationTextKey'),
          e => {
            stopPropagation(e)
            massAction({ action, ids: item.id })
          },
          t('Modal.ProviderArchiveBtnKey')
        )
        break
      case PROVIDER_ACTION_EDIT_BUILDINGS_AND_CATEGORIES:
        showEditBuildingAndCategoryModal({
          action: PROVIDER_ACTION_EDIT_BUILDINGS_AND_CATEGORIES,
          id: item.id,
          name: item.name,
        })
        break
      default:
        return null
    }
  }

  function setMassAction(action) {
    switch (action) {
      case PROVIDER_ACTION_ARCHIVE_MASS:
        showConfirmModal(
          t('Modal.ProviderArchiveConfirmationTextKey'),
          e => {
            stopPropagation(e)
            massAction({ action })
          },
          t('Modal.ProviderArchiveBtnKey')
        )
        break
      case PROVIDER_ACTION_RESTORE_MASS:
        showConfirmModal(
          t('Modal.ProviderRestoreConfirmationTextKey'),
          e => {
            stopPropagation(e)
            massAction({ action })
          },
          t('Modal.ProviderRestoreBtnKey')
        )
        break
      case PROVIDER_ACTION_MASS_EMAIL:
        handleOpenCreationEmail()
        break
      case PROVIDER_ACTION_EDIT_BUILDINGS_AND_CATEGORIES_MASS:
        showEditBuildingAndCategoryModal({
          action: PROVIDER_ACTION_EDIT_BUILDINGS_AND_CATEGORIES_MASS,
        })
        break
      default:
        return null
    }
  }

  function getProviderItemActions(item) {
    return getProviderTableItemActions({ item })
  }

  return {
    getProviderItemActions,
    isPerformingMassAction,
    setItemAction,
    setMassAction,
  }
}
